// import "./styles.css";
import { FC, useEffect, useState } from "react";
import { Grid, Tab, Tabs, Typography, Box, IconButton } from "@mui/material";
import { TitleBarProps } from "../../../../shared/components/titleBar";
import TabPanel from "../../../../shared/components/tabpanel/tabpanel";
import TwoFactorAuth from "./two-factor-auth";
import ProfileEditView from "./profile";
import ChangePassword from "./changePassword";
import { ErrorMessage } from "../../../../shared/components/errorMessage";
import useQuery from "../../../../shared/components/hooks/useQuery";
import { PersonalProfile } from "../../../../shared/interfaces/personal-profile";
import useMutation from "../../../../shared/components/hooks/useMutation";

import { FormViewContainer } from "../../../../shared/components/formViewContainer";
import {
  ImageCircledPreview,
  ProfileBorderBox,
  ProfileContainer,
  ProfileDetailsBox,
  ProfileViewContainer,
} from "../../../../shared/components/profileComponents";

const OrganizationDetails: FC = () => {
  const {
    loading,
    data,
    fetchData,
    error: personalError,
  } = useQuery(`/admin/user/profile`, null);

  const profileData: PersonalProfile = (data as any)?.data || {};

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [errors, setErrors] = useState<string | null>(null);

  const { modifyData } = useMutation();

  // Update the preview URL when profile data is fetched
  useEffect(() => {
    if (profileData?.admin?.profilePicture) {
      setPreviewUrl(profileData.admin.profilePicture);
    }
  }, [profileData]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const FileUpload = async (file: File) => {
    const formData = new FormData();
    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };
    formData.append("file", file);

    try {
      const responseData = await modifyData(
        `admin/user/upload-profile-picture`,
        "post",
        formData,
        config
      );

      if (responseData?.data.fileUrl) {
        setPreviewUrl(responseData.data.fileUrl);
      }
    } catch (error) {
      setErrors("Failed to upload image. Please try again.");
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    const error = validateImage(file);
    if (error) {
      setErrors(error);
      return;
    }
    setErrors(null);
    if (file) {
      FileUpload(file);
    }
  };

  const validateImage = (file: File | null) => {
    if (!file) {
      return "Image is required";
    }
    if (!["image/jpeg", "image/png", "image/gif"].includes(file.type)) {
      return "Invalid file type. Please select a valid image.";
    }
    const maxSizeInBytes = 2 * 1024 * 1024; // 2 MB in bytes
    if (file.size > maxSizeInBytes) {
      return "File size exceeds the 2 MB limit. Please upload a smaller file.";
    }
    return null;
  };

  const viewTitleBar: TitleBarProps = {
    title: "User Profile",
    headerTitle: "Personal Details",
    subHeading: "This is the demo text for org details",
  };

  return (
    <ProfileBorderBox>
      <div>
        <FormViewContainer loading={loading} titleBar={viewTitleBar}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="start"
          >
            <Grid item md={1.5} ml={2}>
              <ImageCircledPreview>
                <img
                  src={
                    previewUrl ||
                    "https://cdn-icons-png.flaticon.com/512/3607/3607444.png"
                  }
                  alt="Preview"
                />
                <IconButton
                  className="icon-button"
                  size="small"
                  component="label"
                >
                  <input
                    type="file"
                    hidden
                    id="image-upload"
                    name="image"
                    accept="image/*"
                    onChange={(e) => {
                      handleFileChange(e);
                    }}
                  />{" "}
                  +
                </IconButton>
                {errors && (
                  <Grid>
                    {" "}
                    <ErrorMessage message={errors ?? null} />
                  </Grid>
                )}
              </ImageCircledPreview>
            </Grid>
            <Grid item xs={5}>
              <ProfileDetailsBox>
                <Typography variant="h3">{profileData?.admin?.name}</Typography>
                <Typography variant="body2">{profileData?.phone}</Typography>
                <Typography variant="body2">{profileData?.email}</Typography>
              </ProfileDetailsBox>
            </Grid>
          </Grid>{" "}
        </FormViewContainer>
      </div>
      <Box style={{ padding: "16px", marginTop: "15px" }}>
        <Grid
          item
          container
          spacing={2}
          alignItems="stretch"
          justifyContent="space-between"
        >
          <Grid item xs={3.5}>
            <ProfileContainer>
              {!personalError && !loading && (
                <ProfileEditView
                  fetchData={fetchData}
                  profileData={profileData}
                  entityPermission={(data as any)?.entityPermissions}
                />
              )}
            </ProfileContainer>
          </Grid>
          <Grid item xs={8.5}>
            <ProfileViewContainer>
              <FormViewContainer
                tabs={
                  <Tabs
                    sx={{ marginBottom: "42px" }}
                    value={activeTab}
                    onChange={handleTabChange}
                  >
                    <Tab label="Change Password" />
                    <Tab label="Two-Factor Authentication" />
                  </Tabs>
                }
              >
                <TabPanel value={activeTab} index={0}>
                  <ChangePassword />
                </TabPanel>
                <TabPanel value={activeTab} index={1}>
                  <TwoFactorAuth />
                </TabPanel>
              </FormViewContainer>
            </ProfileViewContainer>
          </Grid>
        </Grid>
      </Box>
    </ProfileBorderBox>
  );
};

export default OrganizationDetails;
